// QuestionSingleCheckbox.js
import React from "react";
import {
  QuestionCheckboxModel,
  QuestionFactory,
  Serializer,
  ItemValue,
} from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

const QUESTION_TYPE = "singleCheckbox";
let isRegistered = false;

/* =========================================================
   QUESTION MODEL - Single checkbox (Yes/No style)
========================================================= */
class QuestionSingleCheckboxModel extends QuestionCheckboxModel {
  constructor(name) {
    super(name);

    // Initialize with exactly ONE choice
    this.createNewArray("choices");

    // Default single choice
    const defaultChoice = new ItemValue("yes", "Yes");
    this.setPropertyValue("choices", [defaultChoice]);

    // Set other defaults
    this.showSelectAllItem = false;
    this.showNoneItem = false;
    this.showOtherItem = false;
    this.colCount = 1;

    // Custom property for label
    this.checkboxLabel = "Yes";
  }

  getType() {
    return QUESTION_TYPE;
  }

  // Custom property for checkbox label
  get checkboxLabel() {
    return this.getPropertyValue("checkboxLabel", "Yes");
  }

  set checkboxLabel(val) {
    this.setPropertyValue("checkboxLabel", val);

    // Update the single choice when label changes
    if (this.choices.length > 0) {
      this.choices[0].text = val;
    } else {
      const newChoice = new ItemValue("yes", val);
      this.setPropertyValue("choices", [newChoice]);
    }
  }

  // Get the single checkbox value
  get isChecked() {
    return this.value === "yes" || this.value === true;
  }

  set isChecked(val) {
    this.value = val ? "yes" : null;
  }

  // Override to prevent adding multiple choices
  set choices(val) {
    if (Array.isArray(val) && val.length > 0) {
      // Only keep the first choice
      const firstChoice = val[0];
      const itemValue =
        firstChoice instanceof ItemValue
          ? firstChoice
          : new ItemValue(
              firstChoice.value || "yes",
              firstChoice.text || this.checkboxLabel
            );

      this.setPropertyValue("choices", [itemValue]);
    } else {
      // Keep default single choice
      const defaultChoice = new ItemValue("yes", this.checkboxLabel);
      this.setPropertyValue("choices", [defaultChoice]);
    }
  }

  get choices() {
    let choices = this.getPropertyValue("choices");
    if (!choices || choices.length === 0) {
      const defaultChoice = new ItemValue("yes", this.checkboxLabel);
      choices = [defaultChoice];
      this.setPropertyValue("choices", choices);
    }
    return choices;
  }

  // Override to ensure only one value can be selected
  set value(newValue) {
    if (newValue === true || newValue === "yes") {
      super.value = ["yes"];
    } else if (
      newValue === false ||
      newValue === null ||
      newValue === undefined
    ) {
      super.value = [];
    } else if (Array.isArray(newValue) && newValue.length > 0) {
      // Only take the first value if array is provided
      super.value = [newValue[0]];
    } else {
      super.value = [];
    }
  }

  get value() {
    const val = super.value;
    return Array.isArray(val) && val.length > 0 ? val[0] : null;
  }
}

/* =========================================================
   REACT RENDERER - Single checkbox with custom styling
========================================================= */
class SurveyQuestionSingleCheckbox extends SurveyQuestionElementBase {
  get question() {
    return this.props.question;
  }

  handleChange = (e) => {
    const isChecked = e.target.checked;
    this.question.isChecked = isChecked;
  };

  renderElement() {
    const q = this.question;
    const isChecked = q.isChecked;
    const checkboxId = `${q.id}_checkbox`;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          padding: "8px 0",
          width: "100%",
        }}
      >
        {/* Custom checkbox */}
        <div
          style={{
            position: "relative",
            marginRight: "12px",
            flexShrink: 0,
          }}
        >
          <input
            type="checkbox"
            id={checkboxId}
            checked={isChecked}
            onChange={this.handleChange}
            disabled={q.isReadOnly}
            style={{
              width: "20px",
              height: "20px",
              cursor: q.isReadOnly ? "not-allowed" : "pointer",
              opacity: q.isReadOnly ? 0.6 : 1,
              margin: 0,
            }}
          />
          {/* Custom checkmark */}
          {isChecked && (
            <div
              style={{
                position: "absolute",
                top: "4px",
                left: "7px",
                width: "6px",
                height: "12px",
                border: "solid white",
                borderWidth: "0 3px 3px 0",
                transform: "rotate(45deg)",
                pointerEvents: "none",
              }}
            />
          )}
        </div>

        {/* Label */}
        <label
          htmlFor={checkboxId}
          style={{
            cursor: q.isReadOnly ? "not-allowed" : "pointer",
            fontSize: "16px",
            color: q.isReadOnly ? "#6c757d" : "#212529",
            userSelect: "none",
            flex: 1,
            marginTop: "2px",
          }}
          onClick={(e) => {
            if (!q.isReadOnly) {
              e.preventDefault();
              q.isChecked = !isChecked;
            }
          }}
        >
          {q.checkboxLabel ||
            (q.choices && q.choices[0] ? q.choices[0].text : "Yes")}
        </label>

        {/* Required indicator */}
        {q.isRequired && !isChecked && (
          <div
            style={{
              color: "#dc3545",
              fontSize: "12px",
              marginLeft: "8px",
            }}
          >
            *
          </div>
        )}
      </div>
    );
  }
}

/* =========================================================
   REGISTRATION
========================================================= */
export function registerSingleCheckboxQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  // Define custom properties
  Serializer.addClass(
    QUESTION_TYPE,
    [
      {
        name: "name:text",
        category: "general",
        visible: true,
        // ✅ Force text input type
        onSetValue: function (obj, value) {
          obj.name = value;
        },
      },
      {
        name: "title",
        category: "general",
        visible: true,
      },
      {
        name: "description:text",
        category: "general",
        visible: true,
      },
      {
        name: "isRequired:boolean",
        category: "general",
        visible: true,
      },
      {
        name: "readOnly:boolean",
        category: "general",
        visible: true,
      },
      // Single checkbox specific properties
      {
        name: "checkboxLabel:text",
        category: "checkbox",
        default: "Yes",
        displayName: "Checkbox Label",
        visible: true,
      },
      {
        name: "defaultValue:boolean",
        category: "checkbox",
        default: false,
        visible: true,
      },
    ],
    () => new QuestionSingleCheckboxModel(""),
    "checkbox" // Inherit from checkbox but modify behavior
  );

  // Hide unnecessary inherited properties
  const unnecessaryProps = [
    "choices",
    "showSelectAllItem",
    "showNoneItem",
    "showOtherItem",
    "colCount",
    "hasSelectAll",
    "hasNone",
    "hasOther",
  ];

  unnecessaryProps.forEach((propName) => {
    const prop = Serializer.findProperty(QUESTION_TYPE, propName);
    if (prop) {
      prop.visible = false;
      prop.readOnly = true;
    }
  });

  // Handle defaultValue property
  const defaultValueProp = Serializer.findProperty(
    QUESTION_TYPE,
    "defaultValue"
  );
  if (defaultValueProp) {
    defaultValueProp.onSetValue = function (obj, value) {
      obj.defaultValue = value ? ["yes"] : [];
    };
    defaultValueProp.onGetValue = function (obj) {
      return obj.defaultValue && obj.defaultValue.length > 0;
    };
  }

  // Register with QuestionFactory
  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionSingleCheckboxModel(name)
  );

  // Register React component
  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) =>
    React.createElement(SurveyQuestionSingleCheckbox, props)
  );
}

// Auto-register
registerSingleCheckboxQuestion();

export default QuestionSingleCheckboxModel;
